import { useMemo } from "react";
import { Tag, useTagOverflow } from "@sprout/tag";
import Tooltip from "@/sprout/tooltip";
import LoadingSkeleton from "./loading_skeleton.tsx";
import useTranslation from "@/hooks/use_translation";

type Props = {
  loading?: boolean;
  locations?: string[];
  workType?: string | null;
  payRange?: string;
};

const WORK_TYPE_I18N_KEY: Record<string, string> = {
  remote: "workTypeOptions.remote",
  hybrid: "workTypeOptions.hybrid",
  in_person: "workTypeOptions.inPerson",
};

const Tags = ({ loading, locations = [], workType, payRange }: Props) => {
  const { t } = useTranslation("jobSearch");

  const locationTags = useMemo(() => {
    const tags = [...locations];
    if (workType && WORK_TYPE_I18N_KEY[workType]) {
      tags.unshift(t(WORK_TYPE_I18N_KEY[workType]));
    }
    return tags;
  }, [locations, workType, t]);

  const [containerRef, visibleTags, hiddenTags] = useTagOverflow(
    locationTags,
    (tag: string) => (
      <Tag label={tag} truncate={false} className="whitespace-nowrap" maxWidth="max-w-full" />
    ),
    t("moreLocations", { count: 10 }), // Using an arbitrary two-digit count when calculating the overflow text's width
  );

  if (loading) {
    return <LoadingSkeleton className="h-4 w-[120px]" />;
  }

  return (
    <div className="flex flex-col gap-2 flex-grow">
      {locationTags.length > 0 && (
        <div className="flex flex-row gap-2 items-center overflow-hidden" ref={containerRef}>
          {visibleTags.map((tag, index) => (
            <Tag
              key={index}
              label={tag}
              truncate={false}
              className="whitespace-nowrap"
              maxWidth="max-w-full"
            />
          ))}
          {hiddenTags.length > 0 && (
            <Tooltip text={hiddenTags.join(" · ")}>
              <div>{t("moreLocations", { count: hiddenTags.length })}</div>
            </Tooltip>
          )}
        </div>
      )}
      <div className="flex flex-row gap-2 overflow-hidden min-h-[1.25rem]">
        {payRange && (
          <Tag
            label={payRange}
            truncate={false}
            maxWidth="max-w-full"
            color="var(--green-50)"
            className="whitespace-nowrap"
          />
        )}
      </div>
    </div>
  );
};

export default Tags;
